import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import {
  Email,
  Lock,
  Person,
  Phone,
  Badge,
  Visibility,
  VisibilityOff,
  CheckCircle,
  FlightTakeoff,
  Groups,
  Language,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../store';
import { login } from '../store/slices/authSlice';
import api from '../services/api';
import { 
  ServiceIntent, 
  getServiceRoute, 
  getServiceIcon, 
  getServiceById,
  isValidServiceIntent 
} from '../config/services.config';
import { storeServiceAuth } from '../utils/serviceAuth';

interface ServiceAuthProps {
  onAuthenticated: (userData: ServiceUserData) => void;
  intent?: ServiceIntent | null;
  onIntentSelected?: (intent: ServiceIntent) => void;
}

interface ServiceUserData {
  id: string;
  email: string;
  nickname: string;
  name: string;
  phone?: string;
  service_tier: string;
  has_premium_access: boolean;
  token: string;
}

// Helper to safely extract error messages
const getErrorMessage = (error: any, fallback: string): string => {
  if (!error?.response?.data?.detail) {
    return error?.message || fallback;
  }
  
  const detail = error.response.data.detail;
  
  // If detail is a string, return it
  if (typeof detail === 'string') {
    return detail;
  }
  
  // If detail is an array of validation errors
  if (Array.isArray(detail)) {
    return detail.map((err: any) => err.msg || err.message || err).join(', ');
  }
  
  // If detail is an object with msg property
  if (typeof detail === 'object' && detail.msg) {
    return detail.msg;
  }
  
  // Fallback
  return fallback;
};

const ServiceAuth: React.FC<ServiceAuthProps> = ({
  onAuthenticated,
  intent = null,
  onIntentSelected
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userType, setUserType] = useState<'pilot' | 'guest'>('pilot');
  const [activeTab, setActiveTab] = useState(0); // 0: login, 1: register
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);

  // Platform pilot login (email/password)
  const [pilotEmail, setPilotEmail] = useState('');
  const [pilotPassword, setPilotPassword] = useState('');

  // Guest user login (identifier/PIN)
  const [guestIdentifier, setGuestIdentifier] = useState('');
  const [guestPin, setGuestPin] = useState('');

  // Guest registration
  const [regEmail, setRegEmail] = useState('');
  const [regNickname, setRegNickname] = useState('');
  const [regName, setRegName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPin, setRegPin] = useState('');
  const [regPinConfirm, setRegPinConfirm] = useState('');
  const [regLanguage, setRegLanguage] = useState<'it' | 'en'>('it');
  const [emailWarning, setEmailWarning] = useState<string | null>(null);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [showPilotWarningDialog, setShowPilotWarningDialog] = useState(false);
  const [pilotEmailForDialog, setPilotEmailForDialog] = useState('');
  const [showServiceAccountInfoDialog, setShowServiceAccountInfoDialog] = useState(false);
  const [serviceAccountInfo, setServiceAccountInfo] = useState<any>(null);

  // Check if user is logged in as pilot
  const user = useSelector((state: RootState) => state.auth.user);
  const isLoggedIn = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isPilot = user?.is_pilot;
  const isPlatformAdmin = user?.is_platform_admin;
  const isClubManager = user?.user_type === 'club_manager';

  // Helper function to navigate based on intent
  const navigateToService = (serviceIntent: ServiceIntent | null) => {
    if (serviceIntent && isValidServiceIntent(serviceIntent)) {
      const route = getServiceRoute(serviceIntent);
      navigate(route, { replace: true });
    } else {
      // No intent - for guest users go to services portal
      navigate('/app/services', { replace: true });
    }
  };

  // Validate nickname format
  const validateNickname = (nickname: string): boolean => {
    const regex = /^[a-z0-9]+$/;
    return regex.test(nickname.toLowerCase());
  };

  const handleNicknameChange = (value: string) => {
    const lowercaseValue = value.toLowerCase();
    setRegNickname(lowercaseValue);
    
    if (value && !validateNickname(value)) {
      setNicknameError(t('serviceAuth.guestRegister.nicknameError'));
    } else {
      setNicknameError(null);
    }
  };

  // Check email for existing accounts
  const checkEmailStatus = async (email: string) => {
    if (!email || !email.includes('@')) {
      setEmailWarning(null);
      return;
    }

    setEmailCheckLoading(true);
    try {
      const response = await api.get(`/service-auth/check-email/${encodeURIComponent(email)}`);
      
      if (response.data.exists) {
        if (response.data.user_type === 'platform') {
          if (response.data.is_pilot) {
            setEmailWarning(t('serviceAuth.warnings.emailBelongsToPilot'));
            setPilotEmailForDialog(email);
            setShowPilotWarningDialog(true);
            // Clear the form to prevent accidental submission
            setRegEmail('');
          } else {
            setEmailWarning(t('serviceAuth.warnings.emailAlreadyRegistered'));
          }
        } else if (response.data.user_type === 'service') {
          setEmailWarning(t('serviceAuth.warnings.guestAccountExists'));
        }
      } else {
        setEmailWarning(null);
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setEmailWarning(null);
    } finally {
      setEmailCheckLoading(false);
    }
  };

  const handlePilotAutoProvision = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // First, check if there's an unlinked service account
      const checkResponse = await api.get(`/service-auth/check-email/${encodeURIComponent(user?.email || '')}`);
      
      if (checkResponse.data.has_unlinked_service_account) {
        // Link the existing service account
        const linkResponse = await api.post('/service-auth/link-to-pilot');
        
        if (linkResponse.data.linked) {
          setSuccess(t('serviceAuth.success.accountsLinked'));
        }
      }
      
      // Now get the service access
      const response = await api.get('/service-auth/pilot-service-access');
      
      // Create user data object
      const userData: ServiceUserData = {
        id: response.data.service_user_id,
        email: response.data.email,
        nickname: response.data.nickname,
        name: user?.name || response.data.nickname,
        phone: response.data.phone,
        service_tier: 'premium',
        has_premium_access: true,
        token: response.data.service_token,
      };
      
      // Store using utility function (will automatically trigger auth refresh)
      storeServiceAuth(userData);
      
      // Notify parent component
      onAuthenticated(userData);
      
      // Show success message
      if (!checkResponse.data.has_unlinked_service_account) {
        setSuccess(t('serviceAuth.success.welcomePremium'));
      }
      
      // Navigate to service if intent exists
      if (intent) {
        setTimeout(() => navigateToService(intent), 500);
      }
    } catch (error: any) {
      setError(getErrorMessage(error, 'Failed to provision service access'));
    } finally {
      setLoading(false);
    }
  }, [user, onAuthenticated, t, intent, navigate]);

  useEffect(() => {
    // Auto-provision service access for platform users with pilot role
    // This includes platform admins and club managers who are also pilots
    const existingServiceToken = localStorage.getItem('serviceToken');
    const shouldAutoProvision = isLoggedIn && (isPilot || isPlatformAdmin) && intent && !existingServiceToken;
    
    if (shouldAutoProvision) {
      handlePilotAutoProvision();
    }
  }, [isLoggedIn, isPilot, isPlatformAdmin, intent, handlePilotAutoProvision]);

  const handlePilotLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // If there's an intent, handle service auth differently
      if (intent) {
        // Direct API login without Redux to avoid platform auth redirects
        // The /token endpoint expects form-encoded data
        const formData = new URLSearchParams();
        formData.append('username', pilotEmail);
        formData.append('password', pilotPassword);
        
        const loginResponse = await api.post('/token', formData, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });
        
        // Store tokens but don't trigger full platform auth flow
        const { access_token, refresh_token } = loginResponse.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        // Set a flag to prevent platform redirects
        sessionStorage.setItem('serviceLoginInProgress', 'true');
        
        // Get user info from /me endpoint
        const userResponse = await api.get('/me');
        const user = userResponse.data;
        
        // Check if user has pilot privileges (pilot, admin, or club manager with pilot role)
        if (!user.is_pilot && !user.is_platform_admin) {
          setError(t('serviceAuth.errors.pilotOnly'));
          return;
        }
        
        // Get service access for the pilot
        const response = await api.get('/service-auth/pilot-service-access');
        
        // Create user data object
        const userData: ServiceUserData = {
          id: response.data.service_user_id,
          email: response.data.email,
          nickname: response.data.nickname,
          name: user.name || response.data.nickname,
          phone: response.data.phone,
          service_tier: 'premium',
          has_premium_access: true,
          token: response.data.service_token,
        };
        
        // Store using utility function (will automatically trigger auth refresh)
        storeServiceAuth(userData);
        
        // Notify parent component
        onAuthenticated(userData);
        
        setSuccess(t('serviceAuth.success.loginPremiumSuccess'));
        
        // Clear the flag before navigation
        sessionStorage.removeItem('serviceLoginInProgress');
        
        // Navigate directly to service 
        navigateToService(intent);
      } else {
        // No intent - use standard platform login flow
        const result = await dispatch(login({ 
          username: pilotEmail,
          password: pilotPassword,
          login_type: 'pilot'
        }) as any).unwrap();
        
        // Check if user is a pilot
        if (!result.user.is_pilot) {
          setError(t('serviceAuth.errors.pilotOnly'));
          return;
        }
        
        // Redirect based on user role (standard platform login)
        setTimeout(() => {
          if (result.user.user_type === 'club_manager' || result.user.is_platform_admin) {
            navigate('/app/dashboard');
          } else {
            navigate('/app/pilot-dashboard');
          }
        }, 500);
      }
    } catch (error: any) {
      setError(getErrorMessage(error, t('serviceAuth.errors.loginFailed')));
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/service-auth/login', {
        identifier: guestIdentifier,
        pin_code: guestPin,
      });
      
      // Create user data object
      const userData: ServiceUserData = {
        id: response.data.id,
        email: response.data.email,
        nickname: response.data.nickname,
        name: response.data.name,
        service_tier: response.data.service_tier,
        has_premium_access: response.data.has_premium_access,
        token: response.data.token,
      };
      
      // Store using utility function (will automatically trigger auth refresh)
      storeServiceAuth(userData);
      
      // Notify parent component
      onAuthenticated(userData);
      
      setSuccess(t('serviceAuth.success.loginSuccess'));
      
      // Navigate based on intent (guest users only go to services)
      // Add a small delay to ensure localStorage is properly set
      setTimeout(() => {
        // Double-check that auth is stored before navigating
        const storedToken = localStorage.getItem('serviceToken');
        if (storedToken) {
          navigateToService(intent);
        } else {
          console.error('Service token not stored properly after login');
          setError('Authentication storage failed. Please try again.');
        }
      }, 1000);
    } catch (error: any) {
      setError(getErrorMessage(error, t('serviceAuth.errors.invalidCredentials')));
    } finally {
      setLoading(false);
    }
  };

  const handleGuestRegister = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Validate PIN match
      if (regPin !== regPinConfirm) {
        setError(t('serviceAuth.guestRegister.pinMismatch'));
        setLoading(false);
        return;
      }
      
      // Validate PIN format
      if (!/^\d{4}$/.test(regPin)) {
        setError(t('serviceAuth.errors.pinMustBe4Digits'));
        setLoading(false);
        return;
      }

      // First check if email exists as a platform user
      try {
        const checkResponse = await api.get(`/service-auth/check-email/${encodeURIComponent(regEmail)}`);
        if (checkResponse.data.exists) {
          if (checkResponse.data.user_type === 'platform') {
            setError(t('serviceAuth.errors.emailAlreadyRegistered'));
            setLoading(false);
            return;
          } else if (checkResponse.data.user_type === 'service') {
            setError(t('serviceAuth.warnings.guestAccountExists'));
            setLoading(false);
            return;
          }
        }
      } catch (checkError) {
        console.error('Error checking email:', checkError);
        // If check fails, continue with registration
      }
      
      const response = await api.post('/service-auth/register', {
        email: regEmail,
        nickname: regNickname.toLowerCase(), // Send lowercase to backend
        name: regName,
        phone: regPhone || null,
        pin_code: regPin,
        preferred_language: regLanguage,
      });
      
      // Create user data object
      const userData: ServiceUserData = {
        id: response.data.id,
        email: response.data.email,
        nickname: response.data.nickname,
        name: response.data.name,
        service_tier: response.data.service_tier,
        has_premium_access: response.data.has_premium_access,
        token: response.data.token,
      };
      
      // Store using utility function (will automatically trigger auth refresh)
      storeServiceAuth(userData);
      
      // Notify parent component
      onAuthenticated(userData);
      
      setSuccess(t('serviceAuth.success.accountCreated'));
      
      // Navigate based on intent (newly registered guest users)
      // Add a small delay to ensure localStorage is properly set
      setTimeout(() => {
        // Double-check that auth is stored before navigating
        const storedToken = localStorage.getItem('serviceToken');
        if (storedToken) {
          navigateToService(intent);
        } else {
          console.error('Service token not stored properly after registration');
          setError('Authentication storage failed. Please try logging in.');
        }
      }, 1000);
    } catch (error: any) {
      setError(getErrorMessage(error, t('serviceAuth.errors.registrationFailed')));
    } finally {
      setLoading(false);
    }
  };

  // If pilot is already logged in with intent, show loading while auto-provisioning
  if (isLoggedIn && isPilot && intent && loading) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t('serviceAuth.settingUpAccess')}</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom align="center">
        {intent && isValidServiceIntent(intent) ? (
          <>
            {(() => {
              const Icon = getServiceIcon(intent);
              const service = getServiceById(intent);
              return (
                <>
                  {Icon && <Icon sx={{ mr: 1, verticalAlign: 'middle' }} />}
                  {t(service?.name || 'serviceAuth.title')}
                </>
              );
            })()}
          </>
        ) : (
          t('serviceAuth.title')
        )}
      </Typography>
      
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        {intent 
          ? t('serviceAuth.pleaseAuthenticate')
          : t('serviceAuth.authenticateToAccess')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* User Type Selection */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <ToggleButtonGroup
          value={userType}
          exclusive
          onChange={(e, value) => value && setUserType(value)}
          aria-label="user type"
        >
          <ToggleButton value="pilot" aria-label="platform pilot">
            <FlightTakeoff sx={{ mr: 1 }} />
            {t('serviceAuth.userTypes.platformPilot')}
          </ToggleButton>
          <ToggleButton value="guest" aria-label="guest user">
            <Groups sx={{ mr: 1 }} />
            {t('serviceAuth.userTypes.guestUser')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {userType === 'pilot' ? (
        // Platform Pilot Login
        <Box>
          <Typography variant="h6" gutterBottom>
            {t('serviceAuth.pilotLogin.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('serviceAuth.pilotLogin.description')}
          </Typography>
          
          <Box 
            component="form" 
            onSubmit={handlePilotLogin}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              fullWidth
              label={t('serviceAuth.pilotLogin.email')}
              type="email"
              value={pilotEmail}
              onChange={(e) => setPilotEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label={t('serviceAuth.pilotLogin.password')}
              type={showPassword ? 'text' : 'password'}
              value={pilotPassword}
              onChange={(e) => setPilotPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading || !pilotEmail || !pilotPassword}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {loading ? t('serviceAuth.pilotLogin.loggingIn') : t('serviceAuth.pilotLogin.loginButton')}
            </Button>
          </Box>
          
          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            {t('serviceAuth.pilotLogin.notAPilot')} <Link component="button" onClick={() => setUserType('guest')}>{t('serviceAuth.pilotLogin.useGuestAccess')}</Link>
          </Typography>
        </Box>
      ) : (
        // Guest User Login/Register
        <Box>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
            <Tab label={t('serviceAuth.guestLogin.tabLogin')} />
            <Tab label={t('serviceAuth.guestLogin.tabRegister')} />
          </Tabs>
          
          {activeTab === 0 ? (
            // Guest Login
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('serviceAuth.guestLogin.loginDescription')}
              </Typography>
              
              <Box 
                component="form"
                onSubmit={handleGuestLogin}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <TextField
                  fullWidth
                  label={t('serviceAuth.guestLogin.emailOrNickname')}
                  value={guestIdentifier}
                  onChange={(e) => setGuestIdentifier(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  fullWidth
                  label={t('serviceAuth.guestLogin.pin')}
                  type={showPin ? 'text' : 'password'}
                  value={guestPin}
                  onChange={(e) => setGuestPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPin(!showPin)}
                          edge="end"
                        >
                          {showPin ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading || !guestIdentifier || guestPin.length !== 4}
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                  {loading ? t('serviceAuth.guestLogin.loggingIn') : t('serviceAuth.guestLogin.loginButton')}
                </Button>
              </Box>
            </Box>
          ) : (
            // Guest Registration
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('serviceAuth.guestRegister.description')}
              </Typography>
              
              <Box 
                component="form"
                onSubmit={handleGuestRegister}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <TextField
                  fullWidth
                  label={`${t('serviceAuth.guestRegister.email')} *`}
                  type="email"
                  value={regEmail}
                  onChange={(e) => {
                    const newEmail = e.target.value;
                    setRegEmail(newEmail);
                    setEmailWarning(null);
                    // Check email after user stops typing
                    if (newEmail.includes('@')) {
                      const timeoutId = setTimeout(() => {
                        checkEmailStatus(newEmail);
                      }, 500);
                      return () => clearTimeout(timeoutId);
                    }
                  }}
                  onBlur={(e) => checkEmailStatus(e.target.value)}
                  error={!!emailWarning}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                    endAdornment: emailCheckLoading && (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ),
                  }}
                  helperText={emailWarning || t('serviceAuth.guestRegister.emailHelper')}
                />
                
                <TextField
                  fullWidth
                  label={`${t('serviceAuth.guestRegister.nickname')} *`}
                  value={regNickname}
                  onChange={(e) => handleNicknameChange(e.target.value)}
                  error={!!nicknameError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Badge />
                      </InputAdornment>
                    ),
                  }}
                  helperText={nicknameError || t('serviceAuth.guestRegister.nicknameHelper')}
                />
                
                <TextField
                  fullWidth
                  label={`${t('serviceAuth.guestRegister.fullName')} *`}
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  fullWidth
                  label={t('serviceAuth.guestRegister.phone')}
                  value={regPhone}
                  onChange={(e) => setRegPhone(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  select
                  fullWidth
                  label={t('serviceAuth.guestRegister.preferredLanguage')}
                  value={regLanguage}
                  onChange={(e) => setRegLanguage(e.target.value as 'it' | 'en')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Language />
                      </InputAdornment>
                    ),
                  }}
                  helperText={t('serviceAuth.guestRegister.languageHelper')}
                >
                  <MenuItem value="it">Italiano</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </TextField>
                
                <TextField
                  fullWidth
                  label={`${t('serviceAuth.guestRegister.pin')} *`}
                  type={showPin ? 'text' : 'password'}
                  value={regPin}
                  onChange={(e) => setRegPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPin(!showPin)}
                          edge="end"
                        >
                          {showPin ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  fullWidth
                  label={`${t('serviceAuth.guestRegister.confirmPin')} *`}
                  type="password"
                  value={regPinConfirm}
                  onChange={(e) => setRegPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  error={regPinConfirm.length === 4 && regPin !== regPinConfirm}
                  helperText={regPinConfirm.length === 4 && regPin !== regPinConfirm ? t('serviceAuth.guestRegister.pinMismatch') : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={
                    loading || 
                    !regEmail || 
                    !regNickname || 
                    !regName || 
                    regPin.length !== 4 || 
                    regPin !== regPinConfirm
                  }
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                  {loading ? t('serviceAuth.guestRegister.creating') : t('serviceAuth.guestRegister.createButton')}
                </Button>
              </Box>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>{t('serviceAuth.guestRegister.freeTierInfo.title')}</strong>
                  <br />• {t('serviceAuth.guestRegister.freeTierInfo.item1')}
                  <br />• {t('serviceAuth.guestRegister.freeTierInfo.item2')}
                  <br />• {t('serviceAuth.guestRegister.freeTierInfo.item3')}
                </Typography>
              </Alert>
            </Box>
          )}
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="body2" align="center">
            {t('serviceAuth.arePilot')} <Link component="button" onClick={() => setUserType('pilot')}>{t('serviceAuth.loginWithPlatform')}</Link>
          </Typography>
        </Box>
      )}

      {/* Service Account Info Dialog */}
      <Dialog
        open={showServiceAccountInfoDialog}
        onClose={() => setShowServiceAccountInfoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('serviceAuth.serviceAccountInfo.title')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('serviceAuth.serviceAccountInfo.message')}
          </Alert>
          <Typography variant="body1" paragraph>
            {t('serviceAuth.serviceAccountInfo.explanation')}
          </Typography>
          {serviceAccountInfo && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2">
                • {t('serviceAuth.serviceAccountInfo.equipmentItems', { count: serviceAccountInfo.equipment_count })}
              </Typography>
              <Typography variant="body2">
                • {t('serviceAuth.serviceAccountInfo.activeBookings', { count: serviceAccountInfo.active_bookings })}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('serviceAuth.serviceAccountInfo.upgradeNote')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowServiceAccountInfoDialog(false);
              // Continue with pilot registration
            }}
            variant="contained"
          >
            {t('serviceAuth.serviceAccountInfo.understood')}
          </Button>
          <Button
            onClick={() => {
              setShowServiceAccountInfoDialog(false);
              window.location.href = 'mailto:admin@hikeandfly.app?subject=Account%20Migration%20Help';
            }}
          >
            {t('serviceAuth.serviceAccountInfo.contactAdmin')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pilot Warning Dialog */}
      <Dialog
        open={showPilotWarningDialog}
        onClose={() => setShowPilotWarningDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('serviceAuth.pilotWarning.title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('serviceAuth.pilotWarning.message', { email: pilotEmailForDialog })}
          </Alert>
          <Typography variant="body1" paragraph>
            {t('serviceAuth.pilotWarning.explanation')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('serviceAuth.pilotWarning.options')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowPilotWarningDialog(false);
              setUserType('pilot');
              setPilotEmail(pilotEmailForDialog);
            }}
            variant="contained"
            startIcon={<FlightTakeoff />}
          >
            {t('serviceAuth.pilotWarning.loginAsPilot')}
          </Button>
          <Button
            onClick={() => {
              setShowPilotWarningDialog(false);
              // Navigate to password reset
              navigate('/forgot-password', { state: { email: pilotEmailForDialog } });
            }}
            startIcon={<Lock />}
          >
            {t('serviceAuth.pilotWarning.resetPassword')}
          </Button>
          <Button
            onClick={() => setShowPilotWarningDialog(false)}
            color="inherit"
          >
            {t('serviceAuth.pilotWarning.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ServiceAuth;